// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ProjectFilterCallback,
  ProjectGroupingMethod,
  ProjectSearch,
  ProjectSearchValue,
} from '@/project/components/project-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues, Multiple extends boolean = false>
  extends UseControllerProps<Values> {
  multiple?: Multiple;
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;

  /**
   * If provided, this function will be used to filter the project options.
   */
  filter?: ProjectFilterCallback;

  /**
   * If provided, project options will be grouped by these methods in hierarchical order.
   * Multiple methods can be combined to create nested grouping.
   *
   * @example
   * grouping={['active']} // Groups by active status
   * grouping={['active', 'customer']} // Groups by active status, then customer within each status group
   */
  grouping?: ProjectGroupingMethod[];

  /**
   * Callback fired when the user chooses to create a new project.
   * Receives the entered project name as a parameter.
   */
  onCreate?: (name: string) => void;
}

/**
 * A controlled project search component that integrates with react-hook-form.
 */
export function FormProjectSearch<
  Values extends FieldValues,
  Multiple extends boolean = false,
>(props: Props<Values, Multiple>) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined value must be
  // coallesced to null.
  const value =
    field.value ??
    ((props.multiple ? [] : null) as ProjectSearchValue<Multiple>);

  return (
    <ProjectSearch
      multiple={props.multiple}
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      filter={props.filter}
      grouping={props.grouping}
      disabled={props.disabled}
      onChange={field.onChange}
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      onCreate={props.onCreate}
    />
  );
}
